import React, { useRef, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  LineChart as MuiLineChart,
  areaElementClasses,
  lineElementClasses,
} from "@mui/x-charts/LineChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { useDrawingArea, useXScale, useYScale } from "@mui/x-charts/hooks";
import { formatChartTitle, getMetricUnit } from "../../utils";
import { ChartMetricTooltip, ChartTooltipDataContext } from "./ChartMetricTooltip";

interface LineChartData {
  [key: string]: string | number | null | undefined;
}

interface LineSeries {
  dataKey: string;
  label: string;
  color?: string;
  area?: boolean;
  stack?: string;
  dashed?: boolean;
}

/**
 * Risk thresholds for conditional colouring of the min/max envelope.
 * `direction: "below"` means values BELOW the thresholds are concerning
 * (e.g. recovery, sleep — low is bad); `"above"` means high is bad (e.g. strain,
 * resting HR). A band segment is coloured danger/warn when the relevant extreme
 * (min for "below", max for "above") crosses the threshold for that point.
 */
export interface RiskThreshold {
  warn: number;
  danger: number;
  direction: "above" | "below";
}

interface LineChartProps {
  data: LineChartData[];
  xAxisKey?: string;
  series: LineSeries[];
  title?: string;
  height?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  showMinMaxLines?: boolean;
  /**
   * Per-series risk thresholds keyed by dataKey. When set, the min/max envelope
   * is coloured red/orange on segments where the concerning extreme crosses the
   * threshold (see RiskThreshold).
   */
  thresholds?: Record<string, RiskThreshold>;
  /**
   * When provided, this value is used for the min reference line instead of
   * computing min from the displayed (averaged) series data.  Use this to pass
   * the true minimum of the underlying raw data points returned by the API.
   * Only used as fallback when no SD data is available in the dataset.
   */
  referenceMin?: number;
  /**
   * When provided, this value is used for the max reference line instead of
   * computing max from the displayed (averaged) series data.  Use this to pass
   * the true maximum of the underlying raw data points returned by the API.
   * Only used as fallback when no SD data is available in the dataset.
   */
  referenceMax?: number;
  /**
   * Y-axis unit label (e.g. "hrs", "%", "bpm"). When omitted, the unit is
   * derived from the series labels via the metric definitions — shown only when
   * every unit-bearing series shares the same unit. Pass "" to force no label.
   */
  yAxisUnit?: string;
  /**
   * Vertical event markers drawn at matching x-axis values. `axisValue` must
   * equal the x-axis category for that point (i.e. the value under the detected
   * x-axis key — a date/label string). Used for client annotations and
   * abnormal-weather callouts.
   */
  events?: ChartEventMarker[];
  /**
   * Shaded spans across a range of x-axis categories (e.g. a heatwave). Drawn
   * as a translucent band with a pill label, clamped to the visible drawing
   * area so a span that starts before / ends after the window still shows.
   */
  bands?: ChartBand[];
  /**
   * Fired when the user clicks an x-axis position. `axisValue` is the category
   * clicked (date/label string). Enables click-to-annotate on the dashboards.
   */
  onDateClick?: (axisValue: string) => void;
}

export interface ChartEventMarker {
  /** X-axis category to place the marker at (must match a data point's x value). */
  axisValue: string;
  label: string;
  /** Marker line colour; defaults to the theme warning colour. */
  color?: string;
}

export interface ChartBand {
  /** First x-axis category the band covers (already clamped to the window). */
  startAxisValue: string;
  /** Last x-axis category the band covers (inclusive). */
  endAxisValue: string;
  label: string;
  color?: string;
}

/**
 * Calculates a sensible tick step for a Y-axis given the data range.
 * Aims for approximately 5 ticks within the range.
 */
function calculateTickStep(min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 1;
  const rawStep = range / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1;
  else if (normalized <= 2) niceStep = 2;
  else if (normalized <= 5) niceStep = 5;
  else niceStep = 10;
  return niceStep * magnitude;
}

const AreaGradient: React.FC<{ id: string; color: string }> = ({
  id,
  color,
}) => {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
};

/**
 * Renders ±1 SD shaded bands and per-period min/max dashed lines as an SVG
 * overlay inside MUI X Charts.
 * Must be rendered as a child of MuiLineChart so it has access to scale hooks.
 */
const ChartOverlay: React.FC<{
  data: LineChartData[];
  series: LineSeries[];
  seriesWithSd: Set<string>;
  seriesWithMinMax: Set<string>;
  xAxisKey: string;
  clipId: string;
  thresholds?: Record<string, RiskThreshold>;
  dangerColor: string;
  warnColor: string;
}> = ({
  data,
  series,
  seriesWithSd,
  seriesWithMinMax,
  xAxisKey,
  clipId,
  thresholds,
  dangerColor,
  warnColor,
}) => {
  const { left, top, width, height } = useDrawingArea();
  const xScale = useXScale();
  const yScale = useYScale();

  // Risk level of a single extreme value against a threshold.
  const riskLevel = (
    value: number,
    t: RiskThreshold,
  ): "danger" | "warn" | "ok" => {
    if (t.direction === "below") {
      if (value <= t.danger) return "danger";
      if (value <= t.warn) return "warn";
      return "ok";
    }
    if (value >= t.danger) return "danger";
    if (value >= t.warn) return "warn";
    return "ok";
  };

  const toXY = (xVal: unknown, yVal: number): [number, number] | null => {
    const px = (xScale as (v: unknown) => number | undefined)(xVal);
    const py = (yScale as (v: number) => number | undefined)(yVal);
    if (px == null || py == null) return null;
    // xScale and yScale ranges are already in absolute SVG coordinates
    return [px, py];
  };

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect x={left} y={top} width={width} height={height} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
      {series.map((s) => {
        const color = s.color || "#000";
        const elements: React.ReactNode[] = [];

        // ── ±1 SD shaded band ─────────────────────────────────────────────
        if (seriesWithSd.has(s.dataKey)) {
          const upperPts: [number, number][] = [];
          const lowerPts: [number, number][] = [];

          data.forEach((point) => {
            const xVal = point[xAxisKey];
            const val = typeof point[s.dataKey] === "number" ? (point[s.dataKey] as number) : null;
            const sd = typeof point[`${s.dataKey}_sd`] === "number" ? (point[`${s.dataKey}_sd`] as number) : null;
            if (val === null || sd === null || sd <= 0 || xVal == null) return;

            const upper = toXY(xVal, val + sd);
            const lower = toXY(xVal, Math.max(0, val - sd));
            if (upper) upperPts.push(upper);
            if (lower) lowerPts.push(lower);
          });

          if (upperPts.length >= 2) {
            const d = [
              `M ${upperPts[0][0]} ${upperPts[0][1]}`,
              ...upperPts.slice(1).map(([x, y]) => `L ${x} ${y}`),
              ...lowerPts.slice().reverse().map(([x, y]) => `L ${x} ${y}`),
              "Z",
            ].join(" ");

            elements.push(
              <path
                key={`sd-band-${s.dataKey}`}
                d={d}
                fill={color}
                fillOpacity={0.15}
                stroke="none"
              />
            );
          }
        }

        // ── Per-period min/max dashed lines ────────────────────────────────
        if (seriesWithMinMax.has(s.dataKey)) {
          const minPts: [number, number][] = [];
          const maxPts: [number, number][] = [];
          const minRaw: (number | null)[] = [];
          const maxRaw: (number | null)[] = [];

          data.forEach((point) => {
            const xVal = point[xAxisKey];
            if (xVal == null) return;
            const minVal = typeof point[`${s.dataKey}_min`] === "number" ? (point[`${s.dataKey}_min`] as number) : null;
            const maxVal = typeof point[`${s.dataKey}_max`] === "number" ? (point[`${s.dataKey}_max`] as number) : null;
            if (minVal !== null) {
              const pt = toXY(xVal, minVal);
              if (pt) {
                minPts.push(pt);
                minRaw.push(minVal);
              }
            }
            if (maxVal !== null) {
              const pt = toXY(xVal, maxVal);
              if (pt) {
                maxPts.push(pt);
                maxRaw.push(maxVal);
              }
            }
          });

          const threshold = thresholds?.[s.dataKey];

          // Colour for the per-point extreme that drives the risk read:
          // min for "below" thresholds, max for "above".
          const segColor = (rawVal: number | null): string => {
            if (!threshold || rawVal === null) return color;
            const lvl = riskLevel(rawVal, threshold);
            return lvl === "danger" ? dangerColor : lvl === "warn" ? warnColor : color;
          };

          // Draws a dashed polyline, split into per-segment <line>s so each span
          // can take the colour of its starting point's risk level.
          const drawLine = (pts: [number, number][], rawVals: (number | null)[], keyP: string) => {
            if (pts.length < 2) return;
            if (!threshold) {
              elements.push(
                <polyline
                  key={`${keyP}-${s.dataKey}`}
                  points={pts.map(([x, y]) => `${x},${y}`).join(" ")}
                  stroke={color}
                  strokeWidth={1.5}
                  strokeDasharray="5,3"
                  strokeOpacity={0.6}
                  fill="none"
                />,
              );
              return;
            }
            for (let i = 0; i < pts.length - 1; i++) {
              const [x1, y1] = pts[i];
              const [x2, y2] = pts[i + 1];
              // Concerning span if either endpoint is concerning — bias toward the worse.
              const a = segColor(rawVals[i]);
              const b = segColor(rawVals[i + 1]);
              const stroke = a === dangerColor || b === dangerColor
                ? dangerColor
                : a === warnColor || b === warnColor
                  ? warnColor
                  : color;
              elements.push(
                <line
                  key={`${keyP}-${s.dataKey}-${i}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={stroke}
                  strokeWidth={stroke === color ? 1.5 : 2.25}
                  strokeDasharray="5,3"
                  strokeOpacity={stroke === color ? 0.6 : 0.95}
                />,
              );
            }
          };

          drawLine(minPts, minRaw, "min-line");
          drawLine(maxPts, maxRaw, "max-line");
        }

        return elements.length > 0 ? (
          <g key={s.dataKey}>{elements}</g>
        ) : null;
      })}
      </g>
    </>
  );
};

/**
 * Draws shaded bands (e.g. heatwaves) spanning a range of x-axis categories,
 * each with a clear pill label at the top. Rendered inside MuiLineChart so it
 * can use the axis scales. Bands are clipped to the drawing area, so a span
 * that begins before or ends after the visible window still shows as shading.
 */
const BandOverlay: React.FC<{
  bands: ChartBand[];
  xAxisKey: string;
  clipId: string;
  defaultColor: string;
}> = ({ bands, xAxisKey, clipId, defaultColor }) => {
  const { left, top, width, height } = useDrawingArea();
  const xScale = useXScale();
  const px = (v: unknown) =>
    (xScale as (val: unknown) => number | undefined)(v);
  // Half a category step, so bands extend to the visual edge of their end points.
  const bandwidth =
    typeof (xScale as { step?: () => number }).step === "function"
      ? (xScale as { step: () => number }).step() / 2
      : 8;

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <rect x={left} y={top} width={width} height={height} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {bands.map((band, i) => {
          const x0 = px(band.startAxisValue);
          const x1 = px(band.endAxisValue);
          if (x0 == null || x1 == null) return null;
          const startX = Math.max(left, Math.min(x0, x1) - bandwidth);
          const endX = Math.min(left + width, Math.max(x0, x1) + bandwidth);
          const bandW = Math.max(0, endX - startX);
          if (bandW <= 0) return null;
          const color = band.color || defaultColor;
          const labelX = startX + 6;
          const labelW = Math.min(band.label.length * 6.2 + 16, width - 12);

          return (
            <g key={`band-${i}`}>
              <rect
                x={startX}
                y={top}
                width={bandW}
                height={height}
                fill={color}
                fillOpacity={0.12}
              />
              <rect
                x={startX}
                y={top}
                width={2}
                height={height}
                fill={color}
                fillOpacity={0.7}
              />
              <rect
                x={Math.max(endX - 2, startX)}
                y={top}
                width={2}
                height={height}
                fill={color}
                fillOpacity={0.7}
              />
              {/* Pill label */}
              <rect
                x={labelX}
                y={top + 6}
                width={labelW}
                height={18}
                rx={9}
                fill={color}
                fillOpacity={0.95}
              />
              <text
                x={labelX + 8}
                y={top + 6 + 13}
                fontSize={11}
                fontWeight={700}
                fill="#fff"
              >
                {band.label}
              </text>
            </g>
          );
        })}
      </g>
    </>
  );
};

let chartInstanceCounter = 0;

export const LineChart: React.FC<LineChartProps> = ({
  data,
  xAxisKey,
  series,
  title,
  height = 300,
  yAxisMin,
  yAxisMax,
  showMinMaxLines = false,
  referenceMin,
  referenceMax,
  thresholds,
  yAxisUnit,
  events,
  bands,
  onDateClick,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const formattedTitle = formatChartTitle(title);

  // Y-axis unit: explicit prop wins; otherwise derive from series labels and
  // only show when every unit-bearing series agrees on a single unit (mixed
  // units, e.g. HRV vs Stress on one axis, render no label).
  const axisUnit = useMemo(() => {
    if (yAxisUnit !== undefined) return yAxisUnit;
    const units = new Set(
      series.map((s) => getMetricUnit(s.label)).filter((u) => u !== "")
    );
    return units.size === 1 ? Array.from(units)[0] : "";
  }, [yAxisUnit, series]);

  // Detect which series have per-point SD data available in the dataset
  const seriesWithSd = useMemo(() => {
    if (!showMinMaxLines || data.length === 0) return new Set<string>();
    const result = new Set<string>();
    for (const s of series) {
      const sdKey = `${s.dataKey}_sd`;
      const hasData = data.some(
        (d) => d[sdKey] !== null && d[sdKey] !== undefined
      );
      if (hasData) result.add(s.dataKey);
    }
    return result;
  }, [showMinMaxLines, data, series]);

  // Detect which series have per-period min/max data available in the dataset
  const seriesWithMinMax = useMemo(() => {
    if (!showMinMaxLines || data.length === 0) return new Set<string>();
    const result = new Set<string>();
    for (const s of series) {
      const hasData = data.some(
        (d) =>
          (d[`${s.dataKey}_min`] !== null && d[`${s.dataKey}_min`] !== undefined) ||
          (d[`${s.dataKey}_max`] !== null && d[`${s.dataKey}_max`] !== undefined)
      );
      if (hasData) result.add(s.dataKey);
    }
    return result;
  }, [showMinMaxLines, data, series]);


  const detectedXAxisKey = useMemo(() => {
    if (xAxisKey) return xAxisKey;
    if (!data || data.length === 0) return "x";

    const firstItem = data[0];
    const seriesKeys = new Set(series.map((s) => s.dataKey));

    const possibleKeys = ["label", "period", "day", "date", "xAxis", "x"];
    for (const key of possibleKeys) {
      if (firstItem[key] !== undefined && !seriesKeys.has(key)) {
        return key;
      }
    }

    const allKeys = Object.keys(firstItem);
    const nonSeriesKey = allKeys.find((key) => !seriesKeys.has(key));
    return nonSeriesKey || "x";
  }, [data, series, xAxisKey]);

  const chartIdRef = useRef<string>(
    `line-chart-${Date.now()}-${++chartInstanceCounter}-${Math.random()
      .toString(36)
      .substring(2, 9)}`
  );

  const chartId = chartIdRef.current;

  // Compute actual data min/max across all series for dynamic scaling
  const { dataMin, dataMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const item of data) {
      for (const s of series) {
        const val = item[s.dataKey];
        if (typeof val === "number" && isFinite(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
        // Extend Y-axis range to cover the SD band upper bound
        if (seriesWithSd.has(s.dataKey)) {
          const sd = typeof item[`${s.dataKey}_sd`] === "number" ? (item[`${s.dataKey}_sd`] as number) : null;
          if (sd !== null && typeof val === "number") {
            const upper = val + sd;
            const lower = Math.max(0, val - sd);
            if (lower < min) min = lower;
            if (upper > max) max = upper;
          }
        }
        // Extend Y-axis range to cover per-period min/max
        if (seriesWithMinMax.has(s.dataKey)) {
          const minV = typeof item[`${s.dataKey}_min`] === "number" ? (item[`${s.dataKey}_min`] as number) : null;
          const maxV = typeof item[`${s.dataKey}_max`] === "number" ? (item[`${s.dataKey}_max`] as number) : null;
          if (minV !== null && minV < min) min = minV;
          if (maxV !== null && maxV > max) max = maxV;
        }
      }
    }
    return {
      dataMin: min === Infinity ? 0 : min,
      dataMax: max === -Infinity ? 100 : max,
    };
  }, [data, series, seriesWithSd, seriesWithMinMax]);

  // Determine effective Y-axis bounds with padding.
  // When showMinMaxLines is active the axis must extend at least to the
  // reference line values plus a small buffer so the labels are never clipped.
  // True when the chart draws live min/max (or ±SD) overlay bands from the
  // dataset. dataMin/dataMax already include those band values, so the axis
  // must stretch to cover them — otherwise the dashed min/max lines render
  // outside the drawing area and get clipped (i.e. "disappear").
  const hasOverlayBands = seriesWithSd.size > 0 || seriesWithMinMax.size > 0;

  const effectiveYMin = useMemo(() => {
    const range = dataMax - dataMin;
    const padding = Math.max(range * 0.05, 1);
    let base: number;
    if (yAxisMin !== undefined) {
      base = yAxisMin;
    } else {
      base = Math.max(0, Math.floor(dataMin - padding));
    }
    // Live overlay bands present: ensure the axis covers the band minimum even
    // when an explicit yAxisMin was supplied.
    if (showMinMaxLines && hasOverlayBands) {
      base = Math.min(base, Math.max(0, Math.floor(dataMin - padding)));
    }
    // Only use referenceMin for Y-axis extension when there's no live overlay data
    if (showMinMaxLines && !hasOverlayBands && referenceMin !== undefined) {
      base = Math.min(base, Math.floor(referenceMin - padding));
    }
    return base;
  }, [yAxisMin, dataMin, dataMax, showMinMaxLines, referenceMin, hasOverlayBands]);

  const effectiveYMax = useMemo(() => {
    const range = dataMax - dataMin;
    const padding = Math.max(range * 0.05, 1);
    let base: number;
    if (yAxisMax !== undefined) {
      base = yAxisMax;
    } else {
      base = Math.ceil(dataMax + padding);
    }
    // Live overlay bands present: ensure the axis covers the band maximum even
    // when an explicit yAxisMax was supplied.
    if (showMinMaxLines && hasOverlayBands) {
      base = Math.max(base, Math.ceil(dataMax + padding));
    }
    // Only use referenceMax for Y-axis extension when there's no live overlay data
    if (showMinMaxLines && !hasOverlayBands) {
      const refMaxValue = referenceMax !== undefined ? referenceMax : dataMax;
      base = Math.max(base, Math.ceil(refMaxValue + padding));
    }
    return base;
  }, [yAxisMax, dataMin, dataMax, showMinMaxLines, referenceMax, hasOverlayBands]);

  const tickMinStep = useMemo(() => {
    return calculateTickStep(effectiveYMin, effectiveYMax);
  }, [effectiveYMin, effectiveYMax]);

  // Only track area gradient indices for main series (not SD band series)
  const areaSeriesIndices = useMemo(
    () =>
      series
        .map((s, index) => (s.area ? index : null))
        .filter((index): index is number => index !== null),
    [series]
  );

  const muiSeries = useMemo(
    () =>
      series.map((s, index) => ({
        id: `series-${chartId}-${index}`,
        dataKey: s.dataKey,
        label: s.label,
        color: s.color || theme.palette.primary.main,
        area: s.area || false,
        stack: s.stack,
        connectNulls: true,
      })),
    [series, theme.palette.primary.main, chartId]
  );

  // The tooltip is a MUI slot and only sees series ids, so it needs this to get
  // back to the dataset key that the `_min`/`_max` columns are named after.
  const seriesIdToDataKey = useMemo(
    () =>
      Object.fromEntries(
        muiSeries.map((s) => [s.id, s.dataKey] as const)
      ) as Record<string, string>,
    [muiSeries]
  );

  const dashedSeriesIds = useMemo(
    () =>
      series
        .map((s, index) => (s.dashed ? `series-${chartId}-${index}` : null))
        .filter((id): id is string => id !== null),
    [series, chartId]
  );

  const areaSeriesIds = useMemo(
    () =>
      series
        .map((s, index) => (s.area ? `series-${chartId}-${index}` : null))
        .filter((id): id is string => id !== null),
    [series, chartId]
  );

  const chartSx = useMemo(() => {
    const sx: Record<string, unknown> = {};

    if (dashedSeriesIds.length > 0) {
      dashedSeriesIds.forEach((seriesId) => {
        sx[`& .${lineElementClasses.root}[data-series-id="${seriesId}"]`] = {
          strokeDasharray: "5 5",
        };
        sx[`& .${lineElementClasses.root}[data-series="${seriesId}"]`] = {
          strokeDasharray: "5 5",
        };
      });
    }

    if (areaSeriesIds.length > 0) {
      areaSeriesIds.forEach((seriesId) => {
        const seriesIndex = series.findIndex(
          (_, idx) => `series-${chartId}-${idx}` === seriesId
        );
        const seriesConfig = series[seriesIndex];
        const color = seriesConfig.color || theme.palette.primary.main;
        const gradientId = `area-gradient-${chartId}-${seriesIndex}`;

        sx[`& .${areaElementClasses.root}[data-series-id="${seriesId}"]`] = {
          fill: `url(#${gradientId})`,
        };
        sx[`& .${areaElementClasses.root}[data-series="${seriesId}"]`] = {
          fill: `url(#${gradientId})`,
        };
      });
    }

    return sx;
  }, [
    dashedSeriesIds,
    areaSeriesIds,
    series,
    chartId,
    theme.palette.primary.main,
  ]);

  const getGradientIdForSeries = (seriesIndex: number) =>
    `area-gradient-${chartId}-${seriesIndex}`;

  // Use externally-provided reference values (raw data min/max from API) when available,
  // otherwise fall back to computing from the displayed averaged series data.
  const refMin = referenceMin !== undefined ? referenceMin : dataMin;
  const refMax = referenceMax !== undefined ? referenceMax : dataMax;

  const minLabel = `Min: ${Math.round(refMin * 10) / 10}`;
  const maxLabel = `Max: ${Math.round(refMax * 10) / 10}`;

  return (
    <ChartTooltipDataContext.Provider
      value={{
        data: data as Array<Record<string, unknown>>,
        xAxisKey: detectedXAxisKey,
        seriesWithMinMax,
        seriesIdToDataKey,
      }}
    >
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <Box ref={chartRef}>
        <MuiLineChart
          dataset={data}
          height={height}
          onAxisClick={
            onDateClick
              ? (_event, axisData) => {
                  const value = axisData?.axisValue;
                  if (value != null) onDateClick(String(value));
                }
              : undefined
          }
          sx={onDateClick ? { cursor: "pointer", ...chartSx } : chartSx}
          xAxis={[
            {
              scaleType: "point",
              dataKey: detectedXAxisKey,
              disableTicks: true,
            },
          ]}
          yAxis={[
            {
              min: effectiveYMin,
              max: effectiveYMax,
              disableTicks: true,
              tickMinStep: tickMinStep,
              label: axisUnit || undefined,
            },
          ]}
          series={muiSeries}
          margin={{ left: axisUnit ? 12 : 0, right: 0, top: 20, bottom: 20 }}
          grid={{ vertical: true, horizontal: true }}
          slots={{ tooltip: ChartMetricTooltip }}
        >
          {areaSeriesIndices.map((seriesIndex) => {
            const seriesConfig = series[seriesIndex];
            const color = seriesConfig.color || theme.palette.primary.main;
            return (
              <AreaGradient
                key={`gradient-${seriesIndex}`}
                id={getGradientIdForSeries(seriesIndex)}
                color={color}
              />
            );
          })}
          {(seriesWithSd.size > 0 || seriesWithMinMax.size > 0) && (
            <ChartOverlay
              data={data}
              series={series}
              seriesWithSd={seriesWithSd}
              seriesWithMinMax={seriesWithMinMax}
              xAxisKey={detectedXAxisKey}
              clipId={`overlay-clip-${chartId}`}
              thresholds={thresholds}
              dangerColor={theme.palette.error.main}
              warnColor={theme.palette.warning.main}
            />
          )}
          {showMinMaxLines && seriesWithSd.size === 0 && seriesWithMinMax.size === 0 && data.length > 0 && refMin !== refMax && (
            <>
              <ChartsReferenceLine
                y={refMax}
                label={maxLabel}
                labelAlign="end"
                lineStyle={{
                  stroke: theme.palette.text.secondary,
                  strokeDasharray: "6 3",
                  strokeWidth: 1.5,
                  opacity: 0.7,
                }}
                labelStyle={{
                  fontSize: 11,
                  fill: theme.palette.text.secondary,
                }}
              />
              <ChartsReferenceLine
                y={refMin}
                label={minLabel}
                labelAlign="end"
                lineStyle={{
                  stroke: theme.palette.text.secondary,
                  strokeDasharray: "6 3",
                  strokeWidth: 1.5,
                  opacity: 0.7,
                }}
                labelStyle={{
                  fontSize: 11,
                  fill: theme.palette.text.secondary,
                }}
              />
            </>
          )}
          {events?.map((ev, i) => (
            <ChartsReferenceLine
              key={`event-${ev.axisValue}-${i}`}
              x={ev.axisValue}
              label={ev.label}
              labelAlign="start"
              lineStyle={{
                stroke: ev.color || theme.palette.warning.main,
                strokeWidth: 2,
                opacity: 0.9,
              }}
              labelStyle={{
                fontSize: 11,
                fontWeight: 700,
                fill: ev.color || theme.palette.warning.main,
              }}
            />
          ))}
          {bands && bands.length > 0 && (
            <BandOverlay
              bands={bands}
              xAxisKey={detectedXAxisKey}
              clipId={`band-clip-${chartId}`}
              defaultColor={theme.palette.error.main}
            />
          )}
        </MuiLineChart>
      </Box>
    </Box>
    </ChartTooltipDataContext.Provider>
  );
};
